/**
 * Reveal Animation for Gender Party
 * Phase 1 — "countdown": SVG ring timer + number (3, 2, 1)
 * Phase 2 — "reveal": color flash + exploding text
 * Phase 3 — "celebration": big emoji + glow text + canvas confetti
 */

import { useEffect, useState, useRef } from "react";
import type { GenderVote } from "../hooks";
import styles from "./RevealAnimation.module.css";

interface RevealAnimationProps {
  answer: GenderVote;
  onComplete?: () => void;
  className?: string;
}

const RING_RADIUS = 90;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function RevealAnimation({ answer, onComplete, className = "" }: RevealAnimationProps) {
  const [phase, setPhase] = useState<"countdown" | "reveal" | "celebration">("countdown");
  const [countdown, setCountdown] = useState(3);
  // 0..1 progress of the ring (1 = full, drains to 0 over 1s)
  const [ringProgress, setRingProgress] = useState(1);
  const onCompleteRef = useRef(onComplete);
  const ringAnimRef = useRef<number | null>(null);
  const ringStartRef = useRef<number | null>(null);

  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  // Animate ring drain for each second
  useEffect(() => {
    if (phase !== "countdown") return;

    ringStartRef.current = null;
    setRingProgress(1);

    const animate = (ts: number) => {
      if (!ringStartRef.current) ringStartRef.current = ts;
      const elapsed = ts - ringStartRef.current;
      const progress = Math.max(0, 1 - elapsed / 1000);
      setRingProgress(progress);
      if (progress > 0) {
        ringAnimRef.current = requestAnimationFrame(animate);
      }
    };

    ringAnimRef.current = requestAnimationFrame(animate);
    return () => {
      if (ringAnimRef.current) cancelAnimationFrame(ringAnimRef.current);
    };
  }, [countdown, phase]);

  // Countdown tick
  useEffect(() => {
    if (phase !== "countdown") return;

    const timer = setTimeout(() => {
      if ("vibrate" in navigator) navigator.vibrate(80);

      if (countdown > 1) {
        setCountdown((c) => c - 1);
      } else {
        // countdown === 1 → move to reveal
        setPhase("reveal");
        if ("vibrate" in navigator) navigator.vibrate([200, 80, 200, 80, 400]);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [phase, countdown]);

  // Reveal → celebration
  useEffect(() => {
    if (phase !== "reveal") return;
    const timer = setTimeout(() => setPhase("celebration"), 1400);
    return () => clearTimeout(timer);
  }, [phase]);

  // Celebration → complete
  useEffect(() => {
    if (phase !== "celebration") return;
    const timer = setTimeout(() => onCompleteRef.current?.(), 4000);
    return () => clearTimeout(timer);
  }, [phase]);

  const isBoy = answer === "BOY";
  const ringOffset = RING_CIRCUMFERENCE * (1 - ringProgress);

  return (
    <div
      className={`${styles.container} ${isBoy ? styles.boy : styles.girl} ${styles[phase]} ${className}`}
    >
      {/* Phase 1: countdown with SVG ring */}
      {phase === "countdown" && (
        <div className={styles.countdownContainer}>
          <div className={styles.ringWrapper}>
            <svg
              className={styles.ringSvg}
              viewBox="0 0 200 200"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Track */}
              <circle
                cx="100"
                cy="100"
                r={RING_RADIUS}
                stroke="rgba(255,255,255,0.12)"
                strokeWidth="8"
              />
              {/* Progress arc */}
              <circle
                cx="100"
                cy="100"
                r={RING_RADIUS}
                stroke={isBoy ? "#4fc3f7" : "#f48fb1"}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
                transform="rotate(-90 100 100)"
                style={{ transition: "stroke-dashoffset 50ms linear" }}
              />
            </svg>
            <span className={styles.countdownNumber}>{countdown}</span>
          </div>
          <div className={styles.countdownText}>Секунд до раскрытия...</div>
        </div>
      )}

      {/* Phase 2: reveal flash */}
      {phase === "reveal" && (
        <div className={styles.revealContainer}>
          <div className={styles.revealFlash} />
          <div className={styles.revealText}>
            {isBoy ? "МАЛ\u200BЬЧИК!" : "ДЕВ\u200BОЧКА!"}
          </div>
        </div>
      )}

      {/* Phase 3: celebration */}
      {phase === "celebration" && (
        <div className={styles.celebrationContainer}>
          <div className={styles.celebEmoji}>{isBoy ? "👦" : "👧"}</div>
          <div className={styles.celebText}>
            Это {isBoy ? "мальчик" : "девочка"}!
          </div>
          <Confetti isBoy={isBoy} />
        </div>
      )}
    </div>
  );
}

/* ============================================================================= */
/* Confetti — improved: circles + strips + stars                                  */
/* ============================================================================= */

interface ConfettiProps {
  isBoy: boolean;
}

type ParticleShape = "rect" | "circle" | "star";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  shape: ParticleShape;
  opacity: number;
}

function Confetti({ isBoy }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const primary = isBoy ? "#4fc3f7" : "#f48fb1";
    const secondary = isBoy ? "#81d4fa" : "#f8bbd9";
    const accent = isBoy ? "#b3e5fc" : "#fce4ec";
    const colors = [primary, secondary, accent, "#ffffff", "#ffd700", primary, secondary];

    const shapes: ParticleShape[] = ["rect", "rect", "circle", "star"];
    const particles: Particle[] = [];

    for (let i = 0; i < 200; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -Math.random() * canvas.height * 0.5,
        vx: (Math.random() - 0.5) * 9,
        vy: Math.random() * 3 + 1.5,
        size: Math.random() * 10 + 4,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.25,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        opacity: 0.85 + Math.random() * 0.15,
      });
    }

    let animId: number;

    const drawStar = (ctx: CanvasRenderingContext2D, size: number) => {
      const spikes = 5;
      const outer = size;
      const inner = size * 0.4;
      ctx.beginPath();
      for (let s = 0; s < spikes * 2; s++) {
        const r = s % 2 === 0 ? outer : inner;
        const angle = (s * Math.PI) / spikes;
        const px = Math.cos(angle) * r;
        const py = Math.sin(angle) * r;
        if (s === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allBelow = true;
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.08; // gravity
        p.rotation += p.rotationSpeed;
        p.opacity = Math.max(0, p.opacity - 0.0015);

        if (p.y < canvas.height + 20) allBelow = false;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;

        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.shape === "star") {
          drawStar(ctx, p.size / 2);
        } else {
          // rect (strip)
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        }

        ctx.restore();
      });

      if (!allBelow) {
        animId = requestAnimationFrame(animate);
      }
    };

    animate();
    return () => cancelAnimationFrame(animId);
  }, [isBoy]);

  return <canvas ref={canvasRef} className={styles.confettiCanvas} />;
}
